/** @file Hook: HTML5 drag-and-drop of Tree rows — the dragged row, the drop hint, per-row handler props. */
import { useRef, useState, type DragEvent } from 'react';
import { dropPosition, type DropWhere } from './logic/dropPosition';

/** Drop callbacks a Tree accepts; rows are draggable only when `onDrop` is given. */
export interface TreeDropHandlers {
  /** False refuses the drop (no hint, no onDrop); default: any row except the dragged one. */
  canDrop?: ((dragId: string, targetId: string, where: DropWhere) => boolean) | undefined;
  onDrop?: ((dragId: string, targetId: string, where: DropWhere) => void) | undefined;
}

/** Where the dragged row would land. */
export interface DropHint {
  id: string;
  where: DropWhere;
}

/** Props spread on one row element. */
export interface RowDragProps {
  draggable: true;
  onDragStart: (e: DragEvent<HTMLElement>) => void;
  onDragOver: (e: DragEvent<HTMLElement>) => void;
  onDragLeave: (e: DragEvent<HTMLElement>) => void;
  onDrop: (e: DragEvent<HTMLElement>) => void;
  onDragEnd: () => void;
}

const MIME = 'application/x-pose2d-row';
const whereOf = (e: DragEvent<HTMLElement>, rowHeight: number): DropWhere => dropPosition(e.clientY - e.currentTarget.getBoundingClientRect().top, rowHeight);
/** Only drags a tree row started carry MIME; files, text and other widgets are not ours to answer. */
const isRowDrag = (e: DragEvent<HTMLElement>): boolean => Array.from(e.dataTransfer.types).includes(MIME);

/** `rowProps(id)` is undefined when the tree takes no drops; the hint clears on drop, drag end and leaving the row. */
export function useTreeDrag(handlers: TreeDropHandlers, rowHeight: number): { hint: DropHint | null; rowProps: (id: string) => RowDragProps | undefined } {
  const dragged = useRef<string | null>(null);
  const [hint, setHint] = useState<DropHint | null>(null);
  const { canDrop, onDrop } = handlers;
  const allowed = (target: string, where: DropWhere): boolean => {
    const from = dragged.current;
    return from !== null && from !== target && (canDrop?.(from, target, where) ?? true);
  };
  const end = (): void => { dragged.current = null; setHint(null); };
  const rowProps = (id: string): RowDragProps | undefined => onDrop === undefined ? undefined : {
    draggable: true,
    onDragStart: (e) => { dragged.current = id; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData(MIME, id); },
    onDragOver: (e) => {
      if (!isRowDrag(e)) return;
      const where = whereOf(e, rowHeight);
      if (!allowed(id, where)) { if (hint?.id === id) setHint(null); return; }
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (hint?.id !== id || hint.where !== where) setHint({ id, where });
    },
    // dragleave also fires when the pointer crosses from the row into one of its own children: keep the hint then.
    onDragLeave: (e) => { if (hint?.id === id && !e.currentTarget.contains(e.relatedTarget as Node | null)) setHint(null); },
    onDrop: (e) => {
      if (!isRowDrag(e)) return;
      const where = whereOf(e, rowHeight);
      const from = dragged.current;
      // The payload names the row that was dragged: a row of another tree, or a drag whose dragend never came, is refused.
      const ok = allowed(id, where) && e.dataTransfer.getData(MIME) === from;
      e.preventDefault();
      end();
      if (ok) onDrop(from, id, where);
    },
    onDragEnd: end,
  };
  return { hint, rowProps };
}
